import React, {
	useMemo,
	useCallback,
	useState,
	useEffect,
	useRef,
} from "react";
import { useMediaQuery } from "@react-hook/media-query";
import { handleCTAClick } from "@website-builder/utilities/utils/DesignUtils.js";
import { isBusinessVerticalB2U } from "@website-builder/utilities/utils/helpers.js";
import { TABLET_M_BP } from "@website-builder/utilities/constants/style-constants/screen-sizes.js";
const isB2u = isBusinessVerticalB2U();

import {
	Grid,
	Col,
	Text,
	RequestSyllabusButton,
	Separator,
	VerticalList,
	Select,
	Button,
} from "@website-builder/ui/shared/elements";

//styles
import {
	CurriculumWrapper,
	TopicsWrapper,
	ScrollableTopicWrapper,
	CurriculumTopics,
} from "./styles";

const iconConfig = {
	iconName: "checkCircle",
	stroke: "var(--primary-forest-400)",
	size: "1.5rem",
};
const CurriculumWR = ({
	label,
	headline,
	description,
	context = {},
	headerCTA,
	headerCTAText,
	curriculumContent,
	globalVariableWithURL,
	pageSection = "",
	workshopId = "",
	showNumbers = true,
}) => {
	// state
	const [selectedTopicIndex, setSelectedTopicIndex] = useState(0);
	// content reference to enable scroll back to top
	const contentRef = useRef(null);
	const tabRef = useRef(null);

	const [isTopicDivScrolledToEnd, setIsTopicDivScrolledToEnd] = useState(false);
	// checks for mobile width
	const satisfiesMobileWidth = useMediaQuery(`(max-width: ${TABLET_M_BP}px)`);
	const [isMobile, setIsMobile] = useState(false);
	const [removeGradient, setRemoveGradient] = useState(false);

	useEffect(() => {
		setIsMobile(satisfiesMobileWidth);
	}, [satisfiesMobileWidth]);

	const topicsCount = curriculumContent?.curriculumTopics?.length;

	// checks the scroll of topics to enable and disable bottom opacity gradient of div
	const handleScrollOfTopics = useCallback(
		(e) => {
			const reachedBottom =
				e.target.scrollHeight - e.target.scrollTop <=
				e.target.clientHeight + 20;

			setIsTopicDivScrolledToEnd(reachedBottom);
		},
		[isTopicDivScrolledToEnd],
	);

	useEffect(() => {
		const handleFocusIn = (event) => {
			const isButtonInsideWrapper = tabRef?.current?.contains(event?.target);
			setRemoveGradient(isTopicDivScrolledToEnd || isButtonInsideWrapper);
		};
		document?.addEventListener("focusin", handleFocusIn);
		return () => {
			document?.removeEventListener("focusin", handleFocusIn);
		};
	}, [isTopicDivScrolledToEnd, tabRef]);

	const renderTabTopics = () => {
		return (
			<Col start={2} span={3} mdSpan={6} className="flex-column gap1">
				<TopicsWrapper>
					<Text
						color={{
							base: "secondary",
							color: "gray",
							shade: "dark",
						}}
						className="topics-label"
						variant="label_M"
					>
						{curriculumContent?.filterHeading}
					</Text>

					{isMobile ? (
						<Select
							placeholder="Select unit..."
							value={selectedTopicIndex}
							onChange={(event) => {
								setSelectedTopicIndex(Number(event?.target?.value));
							}}
						>
							{curriculumContent?.curriculumTopics.map((item, i) => (
								<option value={i} key={`${i}+${item?.name}`}>
									{`${showNumbers ? `Topic ${i + 1}: ` : ""} ${item?.name}`}
								</option>
							))}
						</Select>
					) : (
						<ScrollableTopicWrapper
							topicsCount={topicsCount}
							className="overlay-wrapper flex-column gap1"
							onScroll={handleScrollOfTopics}
							removeGradient={removeGradient}
							ref={tabRef}
						>
							{curriculumContent?.curriculumTopics.map((topic, index) => {
								const isSelected = selectedTopicIndex === index;
								return (
									<button
										className="topic-button"
										key={`${topic.name}+${index}`}
										onClick={() => {
											contentRef?.current?.scrollIntoView({
												behavior: "smooth",
											});
											setSelectedTopicIndex(index);
										}}
									>
										<Text
											className={`topic-text ${
												isSelected ? "selected-topic" : ""
											}`}
											color={{
												base: "primary",
												color: "neutral-nightshade",
												shade: isSelected ? "800" : "600",
											}}
											variant={isSelected ? "paragraph_S_bold" : "paragraph_S"}
										>
											{showNumbers ? `${topic?.topicNo}: ` : ""}
											{topic?.name}
										</Text>
									</button>
								);
							})}
						</ScrollableTopicWrapper>
					)}
				</TopicsWrapper>
			</Col>
		);
	};

	const RenderTabContent = () => {
		const { curriculumTopics = [] } = curriculumContent;
		return curriculumTopics.map((topic, index) => (
			<CurriculumTopics
				className={selectedTopicIndex === index ? "show" : "hide"}
				key={index}
			>
				<div className="flex-column gap05 topic-header-content">
					<Text
						color={{
							base: "primary",
							color: "neutral-nightshade",
							shade: "800",
						}}
						variant={"headline_5"}
						tag="h3"
					>
						{showNumbers
							? `Topic  ${topic?.topicNo}: ${topic?.name}`
							: topic?.name}
					</Text>
					{topic?.description ? (
						<Text
							color={{
								base: "primary",
								color: "neutral-nightshade",
								shade: "600",
							}}
							variant={"paragraph_M"}
							tag="div"
							className="rich-text-wrapper"
							dangerouslySetInnerHTML={{ __html: topic?.description }}
						/>
					) : null}
				</div>
				{topic?.listItems ? (
					<div>
						{isMobile && <Separator style={{ marginBottom: "1.5rem" }} />}
						<VerticalList
							separator={isMobile}
							iconConfig={iconConfig}
							listItems={topic?.listItems.map(({ text }, index) => {
								return (
									<Text
										key={text + index}
										className="item-header"
										variant="paragraph_M"
										color={{
											base: "primary",
											color: "neutral-nightshade",
											shade: "600",
										}}
										tag="div"
									>
										{text}
									</Text>
								);
							})}
							gap={isMobile ? "1.5rem" : "1rem"}
						/>
						{isMobile && <Separator style={{ marginTop: "1.5rem" }} />}
					</div>
				) : null}
			</CurriculumTopics>
		));
	};

	const tabContent = useMemo(
		() => <RenderTabContent />,
		[selectedTopicIndex, isMobile],
	);

	return (
		<CurriculumWrapper
			className="curriculum outer-margin-wrap"
			topicsCount={topicsCount}
			ref={contentRef}
		>
			<Grid>
				<Col
					start={2}
					span={6}
					mdSpan={6}
					row={1}
					mdRow={1}
					className="flex-column gap1"
				>
					<Text
						color={{
							base: "secondary",
							color: "gray",
							shade: "dark",
						}}
						variant="label_M"
					>
						{label}
					</Text>
					<Text
						color={{
							base: "primary",
							color: "neutral-nightshade",
							shade: "800",
						}}
						variant="headline_3"
						tag="h2"
					>
						{headline}
					</Text>
				</Col>
				{description ? (
					<Col
						start={2}
						span={6}
						mdSpan={6}
						row={2}
						mdRow={2}
						className="header-description-wrapper"
					>
						<Text
							color={{
								base: "primary",
								color: "neutral-nightshade",
								shade: "600",
							}}
							variant="paragraph_M"
							tag="div"
							className="rich-text-wrapper"
							dangerouslySetInnerHTML={{ __html: description }}
						/>
					</Col>
				) : null}
				<Col
					start={description ? 9 : 2}
					span={3}
					mdSpan={6}
					row={2}
					mdRow={3}
					className="header-cta"
				>
					{headerCTAText ? (
						<Text
							color={{
								base: "primary",
								color: "neutral-nightshade",
								shade: "600",
							}}
							variant="paragraph_M_bold"
						>
							{headerCTAText}
						</Text>
					) : null}
					{headerCTA ? (
						!isB2u && headerCTA?.component === "ButtonWR" ? (
							<div className="request-syllabus-btn-wrapper">
								<Button
									{...headerCTA}
									onClick={() =>
										handleCTAClick(headerCTA, {
											page_section: pageSection,
											click_text: headerCTA?.buttonText,
											redirect_to: headerCTA?.link?.link,
										})
									}
								></Button>
							</div>
						) : (
							<div className="request-syllabus-btn-wrapper">
								<RequestSyllabusButton
									{...headerCTA}
									type={headerCTA?.type}
									requestFormType={headerCTA?.requestFormType}
									primaryColorButton={headerCTA?.primaryColorButton}
									pageSection={pageSection}
									context={context}
									workshopId={workshopId}
									cta={headerCTA}
									modalId={`curriculumWR-${workshopId}-request-syllabus-modal`}
								/>
							</div>
						)
					) : null}
				</Col>
				<Col start={2} span={10} mdSpan={6}>
					<Separator style={{ margin: "1.5rem 0" }} />
				</Col>

				{renderTabTopics()}
				<Col
					start={6}
					span={5}
					mdSpan={6}
					className="flex-column gap15 selected-content"
				>
					{tabContent}
				</Col>
			</Grid>
		</CurriculumWrapper>
	);
};

export default CurriculumWR;
